import {myEnrollmentMessagesApi} from '@/api/my-enrollment-messages.api.js'
import {myChannelMessagesApi} from '@/api/my-channel-messages.api.js'
import {Enrollment} from '@/models/enrollment.model.js'
import {Channel} from '@/models/channel.model.js'
import {Message} from '@/models/message.model.js'
import {useUuid} from '@/composables/use-uuid.js'
import {useProvider} from '@/composables/use-provider.js'
import {usePolling} from '@/composables/use-polling.js'
import {mergeMessages} from '@/helpers/messages.js'
import {ref} from 'vue'

/**
 * Helper composable for enrollment messages querying in conversation view.
 * Pass either an enrollment ID or a channel ID and it will load the relevant
 * messages.
 */
export function useEnrollmentMessagesQuerying(channelId, enrollmentId) {

  //Provider detection
  const {currentProvider} = useProvider()

  //Refs
  const enrollment = ref(null)
  const channel = ref(null)
  const messages = ref([])
  const hasMoreMessages = ref(false)

  //Load enrollment messages
  const loadEnrollmentMessages = async () => {
    const data = await myEnrollmentMessagesApi.query(enrollmentId, {limit: 300})
    setEnrollment(data.meta?.enrollment)
    setMessages(data.messages, data.meta)
  }

  //Load channel messages
  const loadChannelMessages = async () => {
    const data = await myChannelMessagesApi.query(channelId, {
      limit: 300,
      provider: currentProvider.value,
    })
    setEnrollment(data.meta?.enrollment)
    setChannel(data.meta?.channel)
    setMessages(data.messages, data.meta)
  }

  //Force update messages ref
  const updateMessages = () => {
    messages.value = [...messages.value]
  }

  //Load messages
  const loadMessages = async () => {
    if (channelId) {
      await loadChannelMessages()
    }
    else if (enrollmentId) {
      await loadEnrollmentMessages()
    }
    else {
      throw new Error('No enrollment or channel ID provided')
    }
  }

  //Test a mock message coming in
  const testMockMessage = () => {
    const {uuid} = useUuid()
    const message = Message.createFrom({
      id: uuid.value,
      body: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. In sit amet elit vel nisi blandit ornare. Maecenas purus quam, dignissim vel quam at, pulvinar efficitur est. Integer egestas porta mauris, quis convallis odio finibus at. Pellentesque non tempor ante. Etiam elit nunc, mollis nec consequat non, dignissim nec lacus. Cras nisl lacus, consequat sit amet tempus vitae, varius eget lacus. Curabitur dui nisi, vestibulum eu volutpat at, tincidunt vitae urna. Vestibulum euismod tellus in imperdiet vehicula. Etiam eu tortor ac ante convallis sagittis.',
      sentAt: (new Date()).toISOString(),
      direction: 'outgoing',
    })
    setTimeout(() => setMessages([message], {total: 1}), 1000)
  }

  //Short-lived lock set after a manual status toggle to prevent a stale poll
  //from immediately overwriting the optimistic local status (see DEV-25493).
  const enrollmentStatusLockUntil = ref(0)
  const lockEnrollmentStatus = (ttlMs = 15000) => {
    enrollmentStatusLockUntil.value = Date.now() + ttlMs
  }

  //Set enrollment
  const setEnrollment = data => {
    if (!data) {
      return
    }
    const fresh = Enrollment.createFrom(data)
    //Do not regress status while a toggle lock is active
    const statusWouldRegress = enrollment.value && fresh.isPaused && !enrollment.value.isPaused
    const lockIsActive = Date.now() < enrollmentStatusLockUntil.value
    if (lockIsActive && statusWouldRegress) {
      fresh.status = enrollment.value.status
      fresh.pausedAt = enrollment.value.pausedAt
    }
    enrollment.value = fresh
  }

  //Set channel
  const setChannel = data => {
    if (data) {
      channel.value = Channel.createFrom(data)
    }
  }

  //Helper to set messages after loading. Optimistic messages sent by the user
  //are confirmed to have been received by the server (and updated with ID,
  //timestamp, etc.) by matching on the externalId we sent along.
  const setMessages = (allMessages, meta) => {

    //Set the has more messages flag
    if (meta?.total > allMessages.length) {
      hasMoreMessages.value = true
    }

    //Merge in the new messages
    messages.value = mergeMessages(messages.value, allMessages, {
      matchOptimistic: (optimistic, incoming) =>
        optimistic.externalId && incoming.externalId === optimistic.externalId,
    })
  }

  //Poll messages
  const POLL_INTERVAL_MS = 10000
  const {
    start: startPollingMessages,
    stop: stopPollingMessages,
  } = usePolling(loadMessages, {interval: POLL_INTERVAL_MS})

  //Expose interface
  return {
    enrollment,
    messages,
    channel,
    loadMessages,
    updateMessages,
    startPollingMessages,
    stopPollingMessages,
    lockEnrollmentStatus,
    testMockMessage,
  }
}
